/** Calendar dates never move between zones; timestamps are displayed in the chosen zone. */
export type DisplayDateValue = Date | string | number | null | undefined;
export type DisplayDateVariant = 'date' | 'dateTime' | 'time' | 'monthDay' | 'shortMonthDay' | 'monthYear';
export interface DatePreferences { dateFormat?: string | null; timeZone?: string | null; locale?: string | null; timeFormat?: string | null; }

export function normalizeDatePattern(value?: string | null): string | null {
  switch (value?.trim().toUpperCase()) {
    case 'DD/MM/YYYY': return 'dd/MM/yyyy';
    case 'MM/DD/YYYY': return 'MM/dd/yyyy';
    case 'YYYY-MM-DD': return 'yyyy-MM-dd';
    case 'MMM-DD-YYYY': return 'MMM-dd-yyyy';
    case 'MMM D, YYYY': case 'MMM D, Y': case 'MMM, D, YYYY': return 'MMM d, yyyy';
    default: return null;
  }
}
export function validTimeZone(value?: string | null): string | null {
  if (!value?.trim()) return null;
  try { new Intl.DateTimeFormat('en', { timeZone: value.trim() }); return value.trim(); } catch { return null; }
}
export function displayTimeZone(value?: string | null): string {
  return validTimeZone(value) ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
}
export function datePickerPattern(value?: string | null): string {
  return ({ 'dd/MM/yyyy': 'dd/mm/yy', 'MM/dd/yyyy': 'mm/dd/yy', 'yyyy-MM-dd': 'yy-mm-dd',
    'MMM-dd-yyyy': 'M-dd-yy', 'MMM d, yyyy': 'M d, yy' } as Record<string, string>)[normalizeDatePattern(value) ?? 'MMM d, yyyy'];
}
export function formatDisplayDate(value: DisplayDateValue, preferences: DatePreferences = {}, variant: DisplayDateVariant = 'date', fallback = '—'): string {
  if (value == null || value === '') return fallback;
  const text = typeof value === 'string' ? value.trim() : null;
  const calendar = text ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(text) : null;
  const unzoned = text && /[T ]\d{2}:\d{2}/.test(text) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const date = calendar ? new Date(`${text}T00:00:00Z`) : new Date(unzoned ? `${text}Z` : value as string | number | Date);
  if (Number.isNaN(date.getTime()) || (calendar && date.toISOString().slice(0, 10) !== text)) return fallback;
  const timeZone = calendar ? 'UTC' : displayTimeZone(preferences.timeZone);
  let locale = preferences.locale?.replace(/_/g, '-') || 'en-US';
  try { new Intl.DateTimeFormat(locale); } catch { locale = 'en-US'; }
  const pattern = normalizeDatePattern(preferences.dateFormat) ?? 'MMM d, yyyy';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date).map(p => [p.type, p.value]));
  const { year, month, day } = parts;
  const monthName = new Intl.DateTimeFormat(locale, { timeZone, month: 'short' }).format(date);
  const formatted = pattern === 'dd/MM/yyyy' ? `${day}/${month}/${year}`
    : pattern === 'yyyy-MM-dd' ? `${year}-${month}-${day}`
    : pattern === 'MMM-dd-yyyy' ? `${monthName}-${day}-${year}`
    : pattern === 'MMM d, yyyy' ? `${monthName} ${Number(day)}, ${year}` : `${month}/${day}/${year}`;
  if (variant === 'monthYear') return `${monthName} ${year}`;
  if (variant === 'monthDay' || variant === 'shortMonthDay') return pattern === 'dd/MM/yyyy' ? `${Number(day)} ${monthName}` : `${monthName} ${Number(day)}`;
  if (variant === 'date' || calendar) return formatted;
  const timeFormat = preferences.timeFormat?.toLowerCase();
  const hour12 = timeFormat?.includes('24') || preferences.timeFormat?.includes('H') ? false
    : timeFormat?.includes('12') || timeFormat?.includes('a') ? true : undefined;
  const time = new Intl.DateTimeFormat(locale, { timeZone, hour: '2-digit', minute: '2-digit', hour12 }).format(date);
  return variant === 'time' ? time : `${formatted}, ${time}`;
}

/** A datetime-local value (yyyy-MM-ddTHH:mm, seconds kept if given) with no offset, as the API's *Local fields take it; null when blank or invalid. */
export function localDateTimeValue(value?: string | null): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2})?$/.exec(value?.trim() ?? '');
  return match && !Number.isNaN(new Date(`${match[1]}T${match[2]}:00Z`).getTime()) ? `${match[1]}T${match[2]}${match[3] ?? ''}` : null;
}
/** The wall clock a timestamp shows in a time zone, as a datetime-local value (yyyy-MM-ddTHH:mm); '' when blank or invalid. */
export function zonedLocalDateTime(value: DisplayDateValue, timeZone?: string | null): string {
  if (value == null || value === '') return '';
  const date = new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: displayTimeZone(timeZone), hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(date).map(p => [p.type, p.value]));
  return `${parts['year']}-${parts['month']}-${parts['day']}T${parts['hour']}:${parts['minute']}`;
}
/** The exact moment a wall-clock time (yyyy-MM-ddTHH:mm) in a time zone stands for; null when invalid. */
export function zonedInstant(local: string | null | undefined, timeZone?: string | null): Date | null {
  const value = localDateTimeValue(local);
  if (!value) return null;
  const guess = new Date(`${value.length === 16 ? `${value}:00` : value}Z`).getTime();
  const offset = (at: number) => new Date(`${zonedLocalDateTime(at, timeZone)}:00Z`).getTime() - Math.floor(at / 60_000) * 60_000;
  const first = guess - offset(guess);
  return new Date(guess - offset(first));
}
/** A browser Date whose own fields hold a wall-clock time, for pickers that edit browser-local Dates. */
export function wallClockDate(local: string | null | undefined): Date | null {
  const value = localDateTimeValue(local);
  if (!value) return null;
  const [y, m, d, h, min] = value.split(/[-T:]/).map(Number);
  return new Date(y, m - 1, d, h, min);
}
/** The wall clock a browser Date's own fields hold (what a picker shows), as yyyy-MM-ddTHH:mm. */
export function localDateTimeOf(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Difference between calendar dates, using the workspace's "today" rather than UTC date parsing or DST hours. */
export function calendarDaysUntil(value: string | null | undefined, timeZone?: string | null, now = new Date()): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? '');
  if (!match || Number.isNaN(now.getTime())) return null;
  const year = Number(match[1]), month = Number(match[2]), day = Number(match[3]);
  const target = new Date(Date.UTC(year, month - 1, day));
  if (target.getUTCFullYear() !== year || target.getUTCMonth() !== month - 1 || target.getUTCDate() !== day) return null;
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: displayTimeZone(timeZone), year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now).map(part => [part.type, part.value]));
  const today = Date.UTC(Number(parts['year']), Number(parts['month']) - 1, Number(parts['day']));
  return (target.getTime() - today) / 86400000;
}
